<template>
    <div class="bg-gray-800 backdrop-blur-md backdrop-filter">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div>
                <div class="flex py-4 items-center justify-between">
                    <!-- Logo (lg+) -->
                    <div class="hidden lg:flex lg:flex-1 lg:items-center">
                        <a href="/">
                            <span class="sr-only">Eichenkreuzliga</span>
                            <img
                                class="h-10 w-auto"
                                src="/2026_EK-Liga_quer_w.png"
                                alt="Logo Eichenkreuzliga">
                        </a>
                    </div>

                    <div class="hidden h-full lg:flex">
                        <!-- Flyout menus -->
                        <PopoverGroup class="inset-x-0 bottom-0 px-4">
                            <div class="flex h-full justify-center gap-x-4">
                                <Popover
                                    v-for="(page) in navigation.pages"
                                    :key="page.name"
                                    class="flex">
                                    <div class="relative flex">
                                        <PopoverButton
                                            class="relative flex items-center justify-center uppercase text-2xl font-medium font-family-squada-one bg-white py-2 px-4 rounded hover:bg-primary-red hover:text-white transition-colors duration-200 ease-out cursor-pointer focus-visible:outline-primary-red">
                                            <div class="bg-white rounded-lg rounded-bl-none p-0.5 mr-2">
                                                <img
                                                    :src="page.logo"
                                                    class="h-10"
                                                    :alt="'Logo ' + page.name">
                                            </div>
                                            {{ page.name }}
                                        </PopoverButton>
                                    </div>
                                    <Transition
                                        enter-active-class="transition ease-out duration-200"
                                        enter-from-class="opacity-0"
                                        enter-to-class=""
                                        leave-active-class="transition ease-in duration-150"
                                        leave-from-class=""
                                        leave-to-class="opacity-0">
                                        <PopoverPanel
                                            v-slot="{ close }"
                                            class="absolute inset-x-0 top-full z-20 w-full bg-white text-sm text-gray-500 mx-auto max-w-7xl shadow-lg">
                                            <!-- Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow -->
                                            <div
                                                class="absolute inset-0 top-1/2 bg-white "
                                                aria-hidden="true" />
                                            <div class="relative bg-white font-semibold">
                                                <div
                                                    class="grid grid-cols-4 gap-y-10 divide-x divide-gray-200">
                                                    <div
                                                        v-for="(section, index) in page.menu"
                                                        :key="section.name"
                                                        class="py-8">
                                                        <div class="group relative">
                                                            <div class="px-8">
                                                                <NuxtLink
                                                                    v-if="isNavigableMenuItem(section)"
                                                                    :to="section.url"
                                                                    class="uppercase font-bold text-gray-900 hover:text-primary-red"
                                                                    @click="close()">
                                                                    {{ section.name }}
                                                                </NuxtLink>
                                                                <span
                                                                    v-else
                                                                    class="uppercase font-bold text-gray-400">{{ section.name }}</span>

                                                                <template v-if="section.menu">
                                                                    <div
                                                                        v-for="group in section.menu.filter(g => (!g.season_id || g.season_id === getSelectedSeasonId(page.sport_id)))"
                                                                        :key="group.name"
                                                                        class="mt-4">
                                                                        <NuxtLink
                                                                            v-if="isNavigableMenuItem(group)"
                                                                            :to="group.url"
                                                                            class="block text-gray-900 font-bold hover:text-primary-red"
                                                                            :class="{'text-primary-red': route.path === group.url}"
                                                                            @click="close();handleMenuItemClick(group)">
                                                                            {{ group.name }}
                                                                        </NuxtLink>
                                                                        <div
                                                                            v-else
                                                                            class="block text-gray-900 font-bold">
                                                                            {{ group.name }}
                                                                        </div>
                                                                        <template v-if="group.menu">
                                                                            <div
                                                                                v-for="slug in group.menu.filter(s => (!s.season_id || s.season_id === getSelectedSeasonId(page.sport_id)))"
                                                                                :key="slug.name"
                                                                                class="mt-2 ml-4">
                                                                                <NuxtLink
                                                                                    v-if="isNavigableMenuItem(slug)"
                                                                                    :to="slug.url"
                                                                                    class="block text-gray-500 font-normal hover:text-primary-red"
                                                                                    :class="{'text-primary-red': route.path === slug.url}"
                                                                                    @click="close();handleMenuItemClick(slug)">
                                                                                    {{ slug.name }}
                                                                                </NuxtLink>
                                                                                <div
                                                                                    v-else
                                                                                    class="block text-gray-900 font-normal">
                                                                                    {{ slug.name }}
                                                                                </div>
                                                                            </div>
                                                                        </template>
                                                                    </div>
                                                                </template>
                                                            </div>
                                                            <div
                                                                v-if="page.menu && index === page.menu.length - 1"
                                                                class="border-t mt-4 pt-4 px-8">
                                                                <BaseSelect
                                                                    :model-value="getSelectedSeasonId(page.sport_id)"
                                                                    :name="$t('menu.selectedSeason')"
                                                                    :label="$t('menu.selectedSeason')+':'"
                                                                    :options="getSeasonsBySportId(page.sport_id)"
                                                                    identifier="id"
                                                                    display-key="name"
                                                                    @update:model-value="(val: number) => setSelectedSeasonId(page.sport_id, val)" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </PopoverPanel>
                                    </Transition>
                                </Popover>
                            </div>
                        </PopoverGroup>
                    </div>

                    <div class="flex flex-1 items-center justify-end gap-x-6">
                        <ServiceMenu
                            v-if="websiteMenuItems?.menuItems"
                            :menu-items="websiteMenuItems.menuItems.filter(item => item.sport_id == null && item.url !== '/infos/') ?? null" />

                        <a
                            v-if="!authStore.isAuthenticated"
                            class="hidden lg:flex items-center gap-x-1 text-xl font-medium font-family-squada-one text-white hover:text-primary-red cursor-pointer"
                            @click="loginSliderOpen = true">
                            <Icon
                                name="login"
                                class="size-6" />
                            Login
                        </a>
                        <UserMenu
                            v-else
                            class="hidden lg:block" />
                    </div>

                    <!-- Logo (lg-) -->
                    <a
                        href="#"
                        class="lg:hidden">
                        <span class="sr-only">Eichenkreuzliga</span>
                        <img
                            class="h-10 w-auto"
                            src="/2026_EK-Liga_quer_w.png"
                            alt="Logo Eichenkreuzliga">
                    </a>

                    <!-- Mobile menu and search (lg-) -->
                    <div class="flex flex-1 items-center justify-end lg:hidden">
                        <button
                            type="button"
                            class="-ml-2 p-2 text-white"
                            @click="mobileMenuOpen = true">
                            <span class="sr-only">Menü öffnen</span>
                            <Icon
                                name="bars"
                                class="size-6"
                                aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile Menu -->
        <BaseSlideOver
            v-model="mobileMenuOpen"
            class="lg:hidden"
            size="sm">
            <div class="flex flex-col gap-y-6">
                <div
                    v-for="page in navigation.pages"
                    :key="page.name">
                    <Disclosure
                        v-slot="{ open }"
                        as="div"
                        :default-open="!!page.url && route.path.startsWith(page.url)"
                        :aria-labelledby="'menu-heading-' + page.name">
                        <h2
                            :id="'menu-heading-' + page.name"
                            class="sr-only">
                            {{ page.name }}
                        </h2>
                        <div class="relative">
                            <DisclosureButton
                                class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                                <div class="flex gap-x-2 items-center">
                                    <img
                                        :src="page.logo"
                                        class="h-8"
                                        :alt="'Logo ' + page.name">
                                    <a class="uppercase text-xl font-family-squada-one">{{ page.name }}</a>
                                </div>

                                <Icon
                                    name="chevron-down"
                                    :class="{'rotate-180': open}"
                                    size="6" />
                            </DisclosureButton>
                        </div>
                        <DisclosurePanel>
                            <div
                                v-for="(section, index) in page.menu"
                                :key="section.name"
                                class="py-4 font-semibold">
                                <div class="group relative">
                                    <div
                                        v-if="index === 0"
                                        class="mb-4 py-4 pl-8">
                                        <BaseSelect
                                            :model-value="getSelectedSeasonId(page.sport_id)"
                                            :name="$t('menu.selectedSeason')"
                                            :label="$t('menu.selectedSeason')+':'"
                                            :options="getSeasonsBySportId(page.sport_id)"
                                            identifier="id"
                                            display-key="name"
                                            @update:model-value="(val: number) => setSelectedSeasonId(page.sport_id, val)" />
                                    </div>

                                    <Disclosure
                                        v-slot="{ open }"
                                        as="div"
                                        class="pl-8"
                                        :default-open="!!section.url && route.path.startsWith(section.url)"
                                        :aria-labelledby="'menu-heading-' + section.name">
                                        <h2
                                            :id="'menu-heading-' + section.name"
                                            class="sr-only">
                                            {{ section.name }}
                                        </h2>
                                        <div class="relative">
                                            <DisclosureButton
                                                class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                                                <div class="flex gap-x-2 items-center">
                                                    <a class="uppercase font-bold text-gray-400">{{ section.name }}</a>
                                                </div>

                                                <Icon
                                                    v-if="section.menu && section.menu.length > 0"
                                                    name="chevron-down"
                                                    :class="{'rotate-180': open}"
                                                    size="6" />
                                            </DisclosureButton>
                                        </div>
                                        <DisclosurePanel v-if="section.menu">
                                            <div
                                                v-for="group in section.menu.filter(g => (!g.season_id || g.season_id === getSelectedSeasonId(page.sport_id)))"
                                                :key="group.name"
                                                class="mt-4">
                                                <Disclosure
                                                    v-slot="{ open }"
                                                    as="div"
                                                    class="pl-8"
                                                    :default-open="!!group.url && route.path.startsWith(group.url)"
                                                    :aria-labelledby="'menu-heading-' + group.name">
                                                    <h2
                                                        :id="'menu-heading-' + group.name"
                                                        class="sr-only">
                                                        {{ group.name }}
                                                    </h2>
                                                    <div class="relative">
                                                        <DisclosureButton
                                                            class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                                                            <div class="flex gap-x-2 items-center">
                                                                <NuxtLink
                                                                    v-if="isNavigableMenuItem(group)"
                                                                    :to="group.url"
                                                                    class="block text-gray-900 font-bold hover:text-primary-red"
                                                                    :class="{'text-primary-red': route.path === group.url}">
                                                                    <div @click="mobileMenuOpen = false;handleMenuItemClick(group)">
                                                                        {{ group.name }}
                                                                    </div>
                                                                </NuxtLink>
                                                                <div
                                                                    v-else
                                                                    class="block text-gray-900 font-bold">
                                                                    {{ group.name }}
                                                                </div>
                                                            </div>

                                                            <Icon
                                                                v-if="group.menu && group.menu.length > 0"
                                                                name="chevron-down"
                                                                :class="{'rotate-180': open}"
                                                                size="6" />
                                                        </DisclosureButton>
                                                    </div>
                                                    <DisclosurePanel v-if="group.menu">
                                                        <div
                                                            v-for="slug in group.menu.filter(s => (!s.season_id || s.season_id === getSelectedSeasonId(page.sport_id)))"
                                                            :key="slug.name"
                                                            class="mt-2 ml-4">
                                                            <NuxtLink
                                                                v-if="isNavigableMenuItem(slug)"
                                                                :to="slug.url"
                                                                class="block text-gray-500 font-normal hover:text-primary-red"
                                                                :class="{'text-primary-red': route.path === slug.url}"
                                                                @click="mobileMenuOpen = false;handleMenuItemClick(slug)">
                                                                {{ slug.name }}
                                                            </NuxtLink>
                                                            <div
                                                                v-else
                                                                class="block text-gray-900 font-normal">
                                                                {{ slug.name }}
                                                            </div>
                                                        </div>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </div>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </div>
                            </div>
                        </DisclosurePanel>
                    </Disclosure>
                </div>

                <Disclosure
                    v-slot="{ open }"
                    as="div"
                    :default-open="route.path.startsWith('/infos/')"
                    aria-labelledby="menu-heading-service">
                    <h2
                        id="menu-heading-service"
                        class="sr-only">
                        Service
                    </h2>
                    <div class="relative">
                        <DisclosureButton
                            class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                            <div class="flex gap-x-2 items-center">
                                <a class="uppercase text-xl font-family-squada-one">Service</a>
                            </div>

                            <Icon
                                name="chevron-down"
                                :class="{'rotate-180': open}"
                                size="6" />
                        </DisclosureButton>
                    </div>


                    <DisclosurePanel v-if="websiteMenuItems && websiteMenuItems.menuItems">
                        <div
                            v-for="group in websiteMenuItems.menuItems.filter(item => item.sport_id == null && item.url !== '/infos/') ?? null"
                            :key="group.name"
                            class="mt-4">
                            <Disclosure
                                v-slot="{ open }"
                                as="div"
                                class="pl-8"
                                :default-open="!!group.url && route.path.startsWith(group.url)"
                                :aria-labelledby="'menu-heading-' + group.name">
                                <h2
                                    :id="'menu-heading-' + group.name"
                                    class="sr-only">
                                    {{ group.name }}
                                </h2>
                                <div class="relative">
                                    <DisclosureButton
                                        class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                                        <div class="flex gap-x-2 items-center">
                                            <NuxtLink
                                                v-if="isNavigableMenuItem(group)"
                                                :to="group.url"
                                                class="block text-gray-900 hover:text-primary-red"
                                                :class="{'text-primary-red': route.path === group.url}"
                                                @click="mobileMenuOpen = false">
                                                {{ group.name }}
                                            </NuxtLink>
                                            <div
                                                v-else
                                                class="block text-gray-900">
                                                {{ group.name }}
                                            </div>
                                        </div>

                                        <Icon
                                            v-if="group.menu && group.menu.length > 0"
                                            name="chevron-down"
                                            :class="{'rotate-180': open}"
                                            size="6" />
                                    </DisclosureButton>
                                </div>
                                <DisclosurePanel v-if="group.menu">
                                    <div
                                        v-for="slug in group.menu"
                                        :key="slug.name"
                                        class="mt-2 ml-4">
                                        <NuxtLink
                                            v-if="isNavigableMenuItem(slug)"
                                            :to="slug.url"
                                            class="block text-gray-500 hover:text-primary-red"
                                            :class="{'text-primary-red': route.path === slug.url}"
                                            @click="mobileMenuOpen = false">
                                            {{ slug.name }}
                                        </NuxtLink>
                                        <div
                                            v-else
                                            class="block text-gray-900">
                                            {{ slug.name }}
                                        </div>
                                    </div>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>
                    </DisclosurePanel>
                </Disclosure>

                <a
                    v-if="!authStore.isAuthenticated"
                    class="flex gap-x-2 items-center uppercase text-xl font-family-squada-one"
                    @click="loginSliderOpen = true">
                    <Icon
                        name="login"
                        class="size-6" />
                    Login
                </a>
                <Disclosure
                    v-else
                    v-slot="{ open }"
                    as="div"
                    aria-labelledby="menu-heading-service">
                    <h2
                        id="menu-heading-service"
                        class="sr-only">
                        {{ authStore.userData?.fullname }}
                    </h2>
                    <div class="relative">
                        <DisclosureButton
                            class="group flex items-center justify-between w-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
                            <div class="flex flex-col items-start">
                                <a class="uppercase text-xl font-family-squada-one">
                                    {{ authStore.userData?.fullname }}
                                </a>

                                <div class="font-normal">
                                    {{ authStore.userData?.organisation_name }}
                                </div>
                            </div>

                            <Icon
                                name="chevron-down"
                                :class="{'rotate-180': open}"
                                size="6" />
                        </DisclosureButton>
                    </div>
                    <DisclosurePanel>
                        <div class="pb-4 px-4 flex flex-col gap-y-2 mt-4">
                            <NuxtLink
                                v-if="publicArea"
                                :to="$localePath('/service/dashboard')">
                                <Icon name="home" />
                                {{ $t('menu.toManagementArea') }}
                            </NuxtLink>
                            <NuxtLink
                                :to="$localePath('/service/users/' + authStore.userData?.id + '/userBaseData')">
                                <Icon name="user" />
                                {{ $t('menu.myProfile') }}
                            </NuxtLink>
                            <a
                                @click="logout">
                                <Icon name="arrow-left-on-rectangle" />
                                {{ $t('menu.logout') }}
                            </a>
                        </div>
                    </DisclosurePanel>
                </Disclosure>
            </div>
        </BaseSlideOver>

        <BaseSlideOver
            v-model="loginSliderOpen"
            :title="authSlideOverTitle"
            size="sm">
            <Component
                :is="authPageVisible"
                :inviting-organisation="invitingOrganisation"
                @auth-page-visible="authPageVisible = $event" />
        </BaseSlideOver>
    </div>
</template>

<script setup lang="ts">
import type {Component} from 'vue'
import AuthForgotPassword from "@/components/pages/auth/AuthForgotPassword.vue"
import AuthLogin from "@/components/pages/auth/AuthLogin.vue"
import AuthRegister from "@/components/pages/auth/AuthRegister.vue"
import AuthResetPassword from "@/components/pages/auth/AuthResetPassword.vue"
import {useAuthStore} from "~/store/auth"
import {usePageStore} from "~/store/page"
import type {
    WebsiteMenuItem,
    WebsiteSeason,
} from "~/types/websiteMenu"

const {t: $t} = useI18n()
const authStore = useAuthStore()
const route = useRoute()
const pageStore = usePageStore()

const mobileMenuOpen = ref<boolean>(false)
const loginSliderOpen = ref<boolean>(false)
const loading = ref(false)

const authPageVisible = shallowRef<Component>(AuthLogin)
const invitingOrganisation = ref<{ name: string } | null>(null)

const authSlideOverTitle = computed(() => {
    switch (authPageVisible.value) {
    case AuthRegister:
        return $t('auth.register')
    case AuthForgotPassword:
        return $t('auth.forgotPassword')
    case AuthResetPassword:
        return $t('auth.resetPassword')
    default:
        return $t('auth.signIn')
    }
})

watch(loginSliderOpen, (isOpen) => {
    if (!isOpen) {
        authPageVisible.value = AuthLogin
        invitingOrganisation.value = null
    }
})

const websiteMenuItems = ref<{ menuItems: WebsiteMenuItem[] } | null>(null)
const allSeasons = ref<WebsiteSeason[]>([])
const {resetSeasonSports, selectedSeasonPerSport, setSelectedSeasonId, saveSelectedSeasonsToStorage} = usePublicSeasonSelection()

function getSeasonsBySportId(sportId: number | undefined): WebsiteSeason[] {
    if (!sportId) return []
    return allSeasons.value.filter(s => s.sport_id === sportId)
}

function getSelectedSeasonId(sportId: number | undefined): number | undefined {
    if (!sportId) return undefined
    return selectedSeasonPerSport.value[sportId]
}

function initDefaultSeasons(): void {
    const sportIds = [...new Set(allSeasons.value.map(s => s.sport_id))]
    for (const sportId of sportIds) {
        const sportSeasons = allSeasons.value.filter(s => s.sport_id === sportId)
        if (sportSeasons.some(season => season.id === selectedSeasonPerSport.value[sportId])) continue
        if (selectedSeasonPerSport.value[sportId] !== undefined && !resetSeasonSports.value.includes(sportId)) resetSeasonSports.value.push(sportId)
        const currentSeason = sportSeasons.find(s => s.current) ?? sportSeasons[0]
        if (currentSeason) {
            selectedSeasonPerSport.value[sportId] = currentSeason.id
        }
    }
    saveSelectedSeasonsToStorage()
}

function handleMenuItemClick(menuItem: WebsiteMenuItem): void {
    if (menuItem && menuItem.season_id) {
        if (route.path === menuItem.url && pageStore.pageData?.menuItem.season_id !== menuItem.season_id) {
            // reload the app to reload page data
            reloadNuxtApp({force: true})
        }
    }
}

function isNavigableMenuItem(item: WebsiteMenuItem): boolean {
    return !!(item.url && (item.contest_id || item.static_page_id || item.url === '/handball/auswertungen/'))
}

const navigation = computed(() => {
    const pages: WebsiteMenuItem[] = [
        {
            name: 'Fußball',
            slug: 'fussball',
            logo: '/fussball.svg',
            url: '/fussball/',
            menu: [],
        }, {
            name: 'Handball',
            slug: 'handball',
            logo: '/handball.svg',
            url: '/handball/',
            menu: [],
        }, {
            name: 'Indiaca',
            slug: 'indiaca',
            logo: '/indiaca.svg',
            url: '/indiaca/',
            menu: [],
        }, {
            name: 'Volleyball',
            slug: 'volleyball',
            logo: '/volleyball.svg',
            url: '/volleyball/',
            menu: [],
        },
    ]

    for (const page of pages) {
        const sportMenuItem = websiteMenuItems.value?.menuItems.find(sport => sport.slug === page.slug)
        page.sport_id = sportMenuItem?.sport_id

        page.menu = websiteMenuItems.value?.menuItems
            .filter(section => section.parent_item_id === sportMenuItem?.id)
            .map(section => ({
                ...section,
                menu: websiteMenuItems.value?.menuItems
                    .filter(group => group.parent_item_id === section.id)
                    .map(group => ({
                        ...group,
                        menu: websiteMenuItems.value?.menuItems
                            .filter(slug => slug.parent_item_id === group.id)
                            .map(slug => ({
                                ...slug,
                            })) ?? [],
                    })) ?? [],
            })) ?? []

        if (page.slug === 'handball') {
            const informationSection = page.menu.find(section => section.slug === 'informationen')
            const evaluationsSection = page.menu.find(section => section.url === '/handball/auswertungen/')

            if (informationSection && evaluationsSection) {
                informationSection.menu = [...(informationSection.menu ?? []), evaluationsSection]
                page.menu = page.menu.filter(section => section !== evaluationsSection)
            }
        }
    }

    return {pages}
})

onMounted(async () => {
    await Promise.all([getMenuItems(), fetchSeasons()])
    initDefaultSeasons()

    if (route.query.invitationToken) {
        await prepareInvitation(route.query.invitationToken as string)
    }
})

async function prepareInvitation(token: string): Promise<void> {
    const response = await $lara.post(useNuxtApp().$apiRoute('auth.invite'), {
        body: {invitation_token: token}
    })
    if (response?.error) return
    invitingOrganisation.value = response?.data ?? null
    authPageVisible.value = AuthRegister
    loginSliderOpen.value = true
}

async function getMenuItems(): Promise<void> {
    const response = await $lara.get(useNuxtApp().$apiRoute('spa.website.pages.index'))
    if (response?.error) return
    websiteMenuItems.value = {
        menuItems: response.menuItems?.data ?? response.menuItems ?? [],
    }
}

async function fetchSeasons(): Promise<void> {
    const response = await $lara.get(useNuxtApp().$apiRoute('spa.frontendSeasons.index'), {
        query: {
            output: 'collectionAll',
            filter: [{
                column: 'visible_in_public_menu',
                operator: '=',
                value: true
            }]
        },
    })
    if (response?.error) return
    allSeasons.value = response?.data ?? response ?? []
}

const publicArea = computed(() => !route.name?.toString().startsWith('service-'))

const logout = async () => {
    loading.value = true
    try {
        await authStore.logout()
    } finally {
        loading.value = false
    }
}
</script>
